import styled from '@emotion/styled';
import {observer} from 'mobx-react-lite';
import {AnimatePresence} from 'motion/react';
import * as motion from 'motion/react-m';
import Head from 'next/head';

import {LayoutTransition} from 'UI/animations/layout';
import {useSonosLayout} from 'UI/layouts/SonosLayout/useSonosLayout';
import {Footer} from 'UI/modules/SonosLayout/Footer';
import {Header} from 'UI/modules/SonosLayout/Header';

import type {themes} from 'UI/utils/theme';

import type {WithChildren} from 'types/global';

/**
 * Props for the `SonosLayout` component, providing customization for the home page flag, theme reactivity, and page title.
 */
interface ComponentProps {
    /**
     * The meta description.
     */
    description?: string | null;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'SonosLayout'
     */
    testId?: string;
    /**
     * The theme data attribute applied to the component for theming purposes.
     */
    theme?: typeof themes;
    /**
     * The title of the page, which is set dynamically based on the current content or section being viewed.
     * This title will be displayed in the browser tab and reflected in the HTML `<title>` tag.
     */
    title?: string | null;
}

/**
 * Provides a layout component for the Sonos product page, integrating the header and dynamic page content.
 * The `SonosLayout` component is a fundamental structure for the Sonos product page, encapsulating the header,
 * dynamic content based on routing, and setting the document's title. It leverages the Next.js `useRouter` hook to
 * respond to route changes, animating content transitions with the `AnimatePresence` component from Framer Motion.
 * This layout ensures that the header remains consistent across different views while allowing the main content to
 * change dynamically, providing a fluid user experience as the user navigates through the site's sections.
 *
 * @param props             The component props.
 * @param props.children    The content to be rendered within the layout. This typically includes the main body of the webpage.
 * @param props.description The meta description.
 * @param props.testId      A unique identifier for testing purposes, aiding in the reliable targeting of the layout component in tests.
 * @param props.title       The title of the page, which is set dynamically based on the current content or section being viewed.
 * @param props.theme       The theme data attribute applied to the component for theming purposes.
 * @returns A JSX.Element representing the structured layout of the page, including the header and dynamically changing content.
 *
 * @example
 * ```tsx
 * <SonosLayout testId="sonos-layout" title="Sonos Ace - Revolutionary Sound Quality" isHome={true}>
 *     <MainSection />
 *     <TechnicalDetails />
 *     <FAQSection />
 * </SonosLayout>
 * ```
 */
const SonosLayout = observer(({
    children,
    description,
    testId = 'SonosLayout',
    theme,
    title
}: WithChildren<ComponentProps>) => {
    const {route} = useSonosLayout();

    return (
        <Wrapper data-cy={testId} data-nfq-theme={theme ?? 'light'}>
            <Head>
                <title>{title}</title>
                {description && <meta content={description} name="description" />}
            </Head>
            <Header />
            <AnimatePresence mode="wait">
                <Content key={route} animate="enter" exit="exit" initial="initial" variants={LayoutTransition}>
                    {children}
                </Content>
            </AnimatePresence>
            <Footer />
        </Wrapper>
    );
});

SonosLayout.displayName = 'SonosLayout';

export {SonosLayout};

const Wrapper = styled.div`
    background-color: ${({theme}) => theme.colors.bgPrimary};
`;

const Content = styled(motion.main)``;